import { useEffect, useRef, useState } from "react";
import styles from "./CaptchaGate.module.scss";

/**
 * Captcha gate for unauthenticated forms (RSVP, future public submissions).
 *
 * When `VITE_HCAPTCHA_SITE_KEY` is set we render the real hCaptcha widget by
 * loading the official script and binding to a container div. The widget calls
 * `onVerify` with the response token when the user solves the challenge.
 *
 * When the env var is unset (dev / CI / preview without provider keys) we fall
 * back to a manual button that produces a sentinel token. This keeps the gate
 * functional in environments that aren't wired to a real captcha provider —
 * the production deployment must set the env var to turn on real protection.
 */
export interface CaptchaGateProps {
  onVerify: (token: string) => void;
  onExpire?: () => void;
}

const SITE_KEY = (
  import.meta.env.VITE_HCAPTCHA_SITE_KEY as string | undefined
)?.trim();

interface HCaptchaGlobal {
  render: (
    container: HTMLElement | string,
    opts: {
      sitekey: string;
      callback: (token: string) => void;
      "expired-callback"?: () => void;
    },
  ) => string;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    hcaptcha?: HCaptchaGlobal;
  }
}

export function CaptchaGate({ onVerify, onExpire }: CaptchaGateProps) {
  if (SITE_KEY) {
    return (
      <HcaptchaWidget
        siteKey={SITE_KEY}
        onVerify={onVerify}
        onExpire={onExpire}
      />
    );
  }
  return <DevCaptchaFallback onVerify={onVerify} />;
}

// ─── Real hCaptcha widget ───────────────────────────────────────────────────

function HcaptchaWidget({
  siteKey,
  onVerify,
  onExpire,
}: {
  siteKey: string;
  onVerify: (token: string) => void;
  onExpire?: () => void;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    function mount() {
      if (cancelled || !containerRef.current || !window.hcaptcha) return;
      widgetIdRef.current = window.hcaptcha.render(containerRef.current, {
        sitekey: siteKey,
        callback: (token: string) => onVerify(token),
        "expired-callback": () => onExpire?.(),
      });
    }

    if (window.hcaptcha) {
      mount();
    } else {
      const existing = document.querySelector<HTMLScriptElement>(
        'script[data-hcaptcha-loader="true"]',
      );
      if (existing) {
        existing.addEventListener("load", mount, { once: true });
      } else {
        const script = document.createElement("script");
        script.src = "https://js.hcaptcha.com/1/api.js?render=explicit";
        script.async = true;
        script.defer = true;
        script.dataset.hcaptchaLoader = "true";
        script.addEventListener("load", mount, { once: true });
        document.head.appendChild(script);
      }
    }

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.hcaptcha) {
        try {
          window.hcaptcha.remove(widgetIdRef.current);
        } catch {
          // ignore — widget may already be torn down
        }
      }
    };
  }, [siteKey, onVerify, onExpire]);

  return <div ref={containerRef} className={styles.widget} />;
}

// ─── Dev / preview fallback ─────────────────────────────────────────────────

function DevCaptchaFallback({
  onVerify,
}: {
  onVerify: (token: string) => void;
}) {
  const [verified, setVerified] = useState(false);

  function handleVerify() {
    setVerified(true);
    // Sentinel token. The API does not verify this in v1 — the rate limiter
    // is the live anti-spam control.
    onVerify("dev-captcha-token");
  }

  if (verified) {
    return (
      <p className={styles.devVerified} role="status">
        Verified
      </p>
    );
  }

  return (
    <button
      type="button"
      onClick={handleVerify}
      className={styles.devButton}
      aria-label="I'm not a robot"
    >
      <span className={styles.devCheckbox} aria-hidden />
      I'm not a robot
    </button>
  );
}
